import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";

import Modal from "../Modal/Modal";
import { useModal } from "../../shared/hooks/useModal";
import GenericTableComponent from "../common/Table";
import { convertToLocalCurrency } from "../../utils/currency";
import uploadIcon from "../../assets/icons/upload.svg";
import errorIcon from "../../assets/icons/errorIcon.svg"
import { useSelector, useDispatch } from "react-redux";

import {
  ProofOfPaymentContainer,
  FileInputLabel,
  FileInput,
  PreviewImage,
} from "../CampaignsComponent/BillTab/BillTabStyles";

import { getInvoices, makePayment } from "../../redux/slices/billingSlice";

const columns = [
  { id: "user", label: "Advertiser" },
  { id: "billingMonth", label: "Billing Month" },
  { id: "createdAt", label: "Bill Date" },
  { id: "conversionCount", label: "Conversion Count" },
  { id: "totalAmount", label: "Amount Due" },
  //{ id: "amountRemaining", label: "Amount Remaining" },
  { id: "status", label: "Bill Status" },
];

const InvoiceTable = () => {
  const dispatch = useDispatch()
  const {invoices, meta, loading} = useSelector((state) => state.billing)
  
  const { showModal, hideModal, isModalOpen } = useModal();
  //const [bills, setBills] = useState(invoices);
  const [selectedBill, setSelectedBill] = useState(null);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [proofOfPayment, setProofOfPayment] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(getInvoices({ page, rowsPerPage }));
  }, [dispatch, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getInvoices({ page: newPage, rowsPerPage }));
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    dispatch(getInvoices({ page: 0, rowsPerPage: newRowsPerPage }));
  };

  const handleViewClick = (id) => {
    const bill = invoices.find((item) => item.id === id);
    if (!bill) return;
    setSelectedBill(bill);
    setIsPartialPayment(false);
    setPaymentAmount("");
    setProofOfPayment(null);
    showModal("invoice-payment");
  };

  const handlePaymentTypeChange = (event) => {
    const value = event.target.value;
    const isPartial = value === "partial";
    setIsPartialPayment(isPartial);
    if (!isPartial && selectedBill) {
      selectedBill.status === "PARTIALLY_PAID" ?
      setPaymentAmount(String(selectedBill.amountRemaining))
      : setPaymentAmount(String(selectedBill.totalAmount))
    } else {
      setPaymentAmount("");
    }
  };

  const handleAmountChange = (event) => {
    const value = event.target.value.replace(/,/g, "");
    // Allow integers and decimals (e.g. 10, 10.7, .5)
    if (!/^\d*\.?\d*$/.test(value)) return;
    setPaymentAmount(value);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProofOfPayment(file);
    }
  };

  const handleSubmitPayment = async (event) => {
    event.preventDefault();
    if (!selectedBill) return;

    const maxAmount = selectedBill.status === "PARTIALLY_PAID" ? selectedBill.amountRemaining : selectedBill.totalAmount;
    const numericAmount = isPartialPayment
      ? Number(paymentAmount || 0)
      : Number(maxAmount || 0);

    const formData = new FormData();
    formData.append("billingId", selectedBill.id);
    formData.append("partyType", "ADVERTISER");
    formData.append("amountPaid", numericAmount);
    formData.append("note", "");
    //formData.append("files", proofOfPayment);
    if (proofOfPayment) {
      formData.append("files", proofOfPayment);
    }else {
      //console.log("File not found")
      Swal.fire({
        title: "Oops..",
        html: "Proof of payment is required",
        icon: null, 
      iconHtml:
          `<img src=${errorIcon} alt="error" style="width:50px;">`,
        confirmButtonText: "Okay",
      });
      return
    }


    console.log(proofOfPayment)
    // return

    try {
      await dispatch(makePayment(formData)).unwrap();
      hideModal("invoice-payment");
      dispatch(getInvoices({ page, rowsPerPage }));
    } catch (error) {
      console.log(error)
    }
  };

  const currentAmountDue = selectedBill
    ? convertToLocalCurrency(selectedBill.totalAmount)  
    : null;

  const formattedEnteredAmount =
    paymentAmount && Number(paymentAmount) > 0
      ? convertToLocalCurrency(Number(paymentAmount))
      : null;

  return (
    <div>
      <GenericTableComponent
        columns={columns}
        data={invoices}
        paginate={true}
        billingView
        showModal={handleViewClick}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={meta?.itemCount || 0}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
      />

      <Modal
        isOpen={isModalOpen("invoice-payment")}
        label="Bill payment"
        handleClose={() => hideModal("invoice-payment")}
      >
        {selectedBill && (
          <PaymentContainer>
          <div className="header">
            <div className="flex">
              <span>●</span>
              <div>
                <h3>{selectedBill.user.firstName} {selectedBill.user.lastName}</h3>
                <p>{selectedBill.user.userReference}</p>
              </div>
            </div>
            <ApprovalLabel status={selectedBill?.status}>
            <span>●</span> {selectedBill?.status}
          </ApprovalLabel>
          </div>

          <div className="amount">
            <p>Amount due:</p>
            <h4>{currentAmountDue}</h4>
          </div>

          <Details>
            <h4>Bill details</h4>
            <div className="flex">
              <label htmlFor="campaign">Billing Month</label>
              <p>{selectedBill.billingMonth}</p>
            </div>
            <div className="flex">
              <label htmlFor="conversions">Conversions</label>
              <p>{selectedBill.conversionCount}</p>
            </div>
            <div className="flex">
              <label htmlFor="clicks">Note</label>
              <p>{selectedBill.note || 'N/A'}</p>
            </div>
          </Details>

          {selectedBill.status === "PARTIALLY_PAID" ? (
            <Details>
            <h4>Partial Payment Details</h4>
            <div className="flex">
              <label htmlFor="clicks">Amount Due</label>
              <p>{convertToLocalCurrency(selectedBill.totalAmount)}</p>
            </div>
            <div className="flex">
              <label htmlFor="campaign">Amount Paid</label>
              <p>{convertToLocalCurrency(selectedBill.totalAmount - selectedBill.amountRemaining)}</p>
            </div>
            <div className="flex">
              <label htmlFor="conversions">Balance</label>
              <p>{convertToLocalCurrency(selectedBill.amountRemaining)}</p>
            </div>
          </Details>
          ) : ''}

          <form onSubmit={handleSubmitPayment}>
            <PaymentSection>
              <h4>Make payment</h4>
              <div className="payment-type">
                <label>
                  <input
                    type="radio"
                    name="payment_type"
                    value="full"
                    checked={!isPartialPayment}
                    onChange={handlePaymentTypeChange}
                  />
                  <span>Full payment</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="payment_type"
                    value="partial"
                    checked={isPartialPayment}
                    onChange={handlePaymentTypeChange}
                  />
                  <span>Part payment</span>
                </label>
              </div>

              <div className="amount-input">
                <label htmlFor="payment_amount">
                  {isPartialPayment ? "Part payment amount" : "Payment amount"}
                </label>
                <input
                  id="payment_amount"
                  type="text"
                  value={
                    selectedBill.status === "PARTIALLY_PAID" ?
                    (isPartialPayment
                      ? paymentAmount
                      : paymentAmount || selectedBill.amountRemaining)
                      :
                    (isPartialPayment
                      ? paymentAmount
                      : paymentAmount || selectedBill.totalAmount)
                  }
                  onChange={isPartialPayment ? handleAmountChange : undefined}
                  readOnly={!isPartialPayment}
                />
                {formattedEnteredAmount && (
                  <small>Entered: {formattedEnteredAmount}</small>
                )}
              </div>
            </PaymentSection>

            <ProofSection>
              <div>
                <label>Proof of payment</label>
                <p>Please upload a receipt of the advertiser’s payment.</p>
              </div>
              <ProofOfPaymentContainer>
                <FileInputLabel htmlFor="invoice_proof_of_payment">
                  {proofOfPayment ? (
                    <PreviewImage
                      src={URL.createObjectURL(proofOfPayment)}
                      alt="Proof of Payment"
                    />
                  ) : (
                    <div className="text">
                      <img src={uploadIcon} alt="" />
                      <p>Click to upload</p>
                    </div>
                  )}
                  <FileInput
                    type="file"
                    id="invoice_proof_of_payment"
                    onChange={handleFileChange}
                  />
                </FileInputLabel>
              </ProofOfPaymentContainer>
            </ProofSection>

            <Footer>
              <div></div>
              <div className="link-buttons">
              <button
                type="button"
                className="see-more"
                onClick={() => hideModal("invoice-payment")}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-campaign"
                disabled={loading || selectedBill.status === "PAID"}
                title={
                  selectedBill.status === "PAID"
                    ? "This bill is already PAID"
                    : undefined
                }
              >
                {loading ? "Submitting..." : "Submit payment"}
              </button>
                {/* <button
                  type="button"
                  className="see-more"
                  onClick={() => hideModal("invoice-payment")}
                >
                  Cancel
                </button>
                <button className="add-campaign" type="submit">
                  Submit payment
                </button> */}
              </div>
            </Footer>
          </form>
        </PaymentContainer>
        )}
      </Modal>
    </div>
  );
};

const PaymentContainer = styled.div`
  padding: 10px;
  max-height: 70vh;
  overflow-y: auto;
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-radius: 4px;
    border: 0.6px solid #b9bdc7;
    width: 100%;
    height: 60px;
    .flex {
      display: flex;
      gap: 5px;
      span {
        color: #48505e;
        font-size: 35px;
      }
      h3 {
        font-size: 16px;
        color: #383e49;
        margin-top: 7px;
        text-transform: capitalize;
      }
      p {
        color: #383e49;
        font-size: 10px;
      }
    }
  }
  .amount {
    display: flex;
    gap: 0.5em;
    margin-top: 30px;
    p {
      font-size: 14px;
      color: #2b2f38;
      margin-top: 5px;
    }
    h4 {
      font-size: 24px;
      color: #2b2f38;
    }
  }
`;

const Details = styled.div`
  margin-top: 30px;
  h4 {
    font-size: 18px;
    color: #101828;
    margin-bottom: 5px;
  }
  .flex {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    label {
      font-size: 12px;
      color: #2b2f38;
    }
    p {
      font-size: 14px;
      color: #2b2f38;
    }
  }
`;

const PaymentSection = styled.div`
  margin-top: 30px;
  h4 {
    font-size: 18px;
    color: #101828;
    margin-bottom: 10px;
  }
  .payment-type {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: #2b2f38;
    }
    input[type="radio"] {
      accent-color: #3730a3;
    }
  }
  .amount-input {
    display: flex;
    flex-direction: column;
    gap: 6px;
    label {
      font-size: 12px;
      color: #2b2f38;
    }
    input {
      padding: 8px 10px;
      border-radius: 4px;
      border: 1px solid #d0d5dd;
      font-size: 14px;
      outline: none;
    }
    input:focus {
      border-color: #3730a3;
    }
    small {
      font-size: 11px;
      color: #667085;
    }
  }
`;

const ProofSection = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  label {
    font-size: 14px;
    color: #2b2f38;
  }
  p {
    font-size: 12px;
    color: #667085;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  padding-bottom: 20px;
  .link-buttons {
    display: flex;
    gap: 10px;
    .see-more,
    .add-campaign {
      padding: 6px 16px;
      height: 32px;
      border: 2px solid #d0d5dd;
      border-radius: 4px;
      font-size: 12px;
      background-color: transparent;
      text-decoration: none;
      cursor: pointer;
      color: #344054;
    }
    .see-more:disabled,
    .add-campaign:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
    .add-campaign {
      color: #fff;
      border: 1px solid #3730a3;
      background-color: #3730a3;
    }
  }
`;

const ApprovalLabel = styled.span`
  display: inline-block;
  font-size: 10px;
  padding: 10px;
  border-radius: 16px;
  font-weight: bold;
  text-align: center;
  text-transform: capitalize;
  span {
    font-size: 15px;
  }
  color: ${({ status }) =>
    status === "PAID"
      ? "#3730a3"
      : status === "REJECTED"
      ? "#FF2D55"
      : "#AF52DE"};

  background-color: ${({ status }) =>
    status === "PAID"
      ? "#ECFDF3"
      : status === "REJECTED"
      ? "#FEF3F2"
      : "rgba(186, 79, 239, 0.12)"};
`;

export default InvoiceTable;